import { PROVIDER_READ_ROLES, PROVIDER_WRITE_ROLES } from './constants'

export const isSupportStaff = (org) => {
  const { access } = org
  if (!access) throw new Error('No access level found for org (isSupportStaff)')
  return access === 'support'
}

// "Manager-level access" — owner, manager, or centre_director. Used to gate
// manager UI surfaces. centre_director is included because it has the same
// operational access as manager (post-QC-2689) — only its initial shift POST
// routes through the approval workflow. If you need an "exact manager"
// check, use isManager() instead.
export const isManagerOrOwner = (org) => {
  if (!org) return false
  const { access } = org
  if (!access) throw new Error('No access level found for org (isManagerOrOwner)')
  return PROVIDER_WRITE_ROLES.includes(access)
}

// "Any provider-side role" — owner, manager, centre_director, or support.
// Use to gate surfaces every provider login owns regardless of write access,
// e.g. their own profile page. For write/employer actions use
// isManagerOrOwner() instead.
export const isProviderMember = (org) => {
  if (!org) return false
  const { access } = org
  if (!access) throw new Error('No access level found for org (isProviderMember)')
  return PROVIDER_READ_ROLES.includes(access)
}

export const isOwner = (org) => {
  if (!org) return false
  const { access } = org
  if (!access) throw new Error('No access level found for org (isOwner)')
  return access === 'owner'
}

export const isManager = (org) => {
  if (!org) return false
  const { access } = org
  if (!access) throw new Error('No access level found for org (isManager)')
  return access === 'manager'
}

// Centre-scoped provider roles: manager, centre_director, support. These users
// see only the centres listed in user_organisations.centres[] (empty = all, per
// the filterByUserCentres convention). Owners always see the whole org.
export const isCentreScopedRole = (org) => {
  if (!org) return false
  const { access } = org
  return access === 'manager' || access === 'centre_director' || access === 'support'
}

// True when the educator/member belongs to the given org. Used to scope
// provider-side actions (e.g. uploading a document) to a manager's own
// organisation — a manager only viewing a cross-org educator gets false.
// Mirrors the backend isEducatorInOrg own-org guarantee.
export const isMemberInOrg = (member, org) =>
  !!member?.organisations?.some((o) => o.organisation_id === org?.organisation_id)


// QC-2927 organisation lock. A self-signed-up organisation is `pending_terms`
// until Ops marks its terms signed; the API surfaces that on every membership
// as `organisation_status` and answers 403 ORG_PENDING_TERMS on provider
// routes while it lasts.
export const isPendingProvider = (org) => org?.organisation_status === 'pending_terms'

// QC-2927: a person stamped as a provider signup (`people.provider_signup_started_at`,
// set when they chose "provider" in the app or first resumed the web flow)
// who has not reached an organisation yet. Logging in anywhere — here or in
// the app — puts them back in the signup until one exists. Anyone who has
// since become a member somewhere, an admin or a QuickCare educator is not
// "mid-signup" whatever the stamp says — the signup itself would refuse them.
export const isProviderSignupInProgress = (user) =>
  !!user?.provider_signup_started_at &&
  !user.is_quickcare_admin &&
  !user.quickcare_educator &&
  !(Array.isArray(user.organisations) && user.organisations.length > 0)

// The membership the dashboard acts as. The dashboard is the provider surface,
// so a login that is both an educator somewhere and an owner/manager elsewhere
// must land on the provider org — `organisations[0]` is whatever order the DB
// returned. Falls back to the first membership (educator / co_only accounts).
export const pickPrimaryOrg = (organisations) => {
  if (!Array.isArray(organisations) || organisations.length === 0) return null
  return organisations.find((org) => PROVIDER_READ_ROLES.includes(org?.access)) || organisations[0]
}
